import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import MembersOnlyBox from "@/components/MembersOnlyBox";
import { PROSE } from "@/lib/prose";
import { formatBytes } from "@/hooks/useClassroom";
import {
  useCommunityPost,
  useCommunityPosts,
  signedCommunityFileUrl,
  formatPostDate,
  monthLabel,
  type CommunityAttachment,
} from "@/hooks/useCommunityPosts";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { SITE } from "@/data/site";
import { cleanSummary, gatedDescription } from "@/lib/summary";
import { ArrowLeft, ArrowRight, Download, ExternalLink, Lock, PlayCircle } from "lucide-react";

/** One non-image attachment: mirrored file (signed download) or a link out. */
const AttachmentButton = ({ a }: { a: CommunityAttachment }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  if (a.kind !== "file" && a.url) {
    const isSkool = a.kind === "skool";
    return (
      <a href={a.url} target="_blank" rel="noopener noreferrer">
        <Button variant="outline" className="w-full justify-between">
          <span className="truncate">{a.label}</span>
          {isSkool ? <PlayCircle className="w-4 h-4 shrink-0" /> : <ExternalLink className="w-4 h-4 shrink-0" />}
        </Button>
      </a>
    );
  }

  const open = async () => {
    if (!a.storage_path) return;
    setBusy(true);
    try {
      const url = await signedCommunityFileUrl(a.storage_path, 120);
      window.location.assign(url);
    } catch (e) {
      toast({
        title: "Could not open that file",
        description: (e as Error).message || "Your access could not be verified. Sign in again and retry.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button className="w-full justify-between" onClick={open} disabled={busy}>
      <span className="truncate">{a.label}</span>
      <span className="flex items-center gap-2 text-xs opacity-80 shrink-0">
        {a.size_bytes ? formatBytes(a.size_bytes) : null}
        <Download className="w-4 h-4" />
      </span>
    </Button>
  );
};

const CommunityResourcePost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useCommunityPost(slug);
  const { data: index } = useCommunityPosts();
  const { user, loading: authLoading } = useAuth();
  const { hasAccess, loading: accessLoading } = usePaymentStatus();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-32 text-center text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-32 text-center">
          <p className="text-muted-foreground mb-6">That post does not exist, or is no longer in the community feed.</p>
          <Link to="/community-resources">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4" /> Community Resource
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { post, attachments, bodyMd } = data;

  const publicSummary = cleanSummary(post.summary === post.title ? "" : post.summary, 600);

  /**
   * Community posts are written by members, so the Article author is the member, not
   * Hector — attributing someone else's write-up to the site owner is the kind of thing
   * that quietly destroys the trust signal it was meant to build. The publisher is still
   * the school.
   */
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${SITE.origin}/community-resources/${post.slug}#article`,
      headline: post.title,
      description: publicSummary || undefined,
      url: `${SITE.origin}/community-resources/${post.slug}`,
      datePublished: post.posted_at,
      dateModified: post.last_changed_at ?? post.posted_at,
      author: post.author_name
        ? { "@type": "Person", name: post.author_name }
        : { "@id": `${SITE.origin}/#organization` },
      publisher: { "@id": `${SITE.origin}/#organization` },
      isPartOf: { "@id": `${SITE.origin}/#website` },
      inLanguage: "en",
      ...(post.cover_url ? { image: post.cover_url } : {}),
      isAccessibleForFree: bodyMd !== null,
      ...(bodyMd === null
        ? {
            hasPart: {
              "@type": "WebPageElement",
              isAccessibleForFree: false,
              cssSelector: ".gated-body",
            },
          }
        : {}),
    },
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Community Resources", path: "/community-resources" },
      { name: post.title, path: `/community-resources/${post.slug}` },
    ]),
  ];
  const gateLoading = authLoading || accessLoading;
  const isMember = !!user && hasAccess;
  const images = attachments.filter((a) => a.kind === "image" && a.url);
  const others = attachments.filter((a) => a.kind !== "image");
  const siblings = (index ?? [])
    .filter((p) => p.posted_month === post.posted_month && p.slug !== post.slug)
    .slice(0, 3);
  const teaser: string[] = [];
  if (post.images_count) teaser.push(`${post.images_count} ${post.images_count === 1 ? "image" : "images"}`);
  if (post.files_count) teaser.push(`${post.files_count} ${post.files_count === 1 ? "file" : "files"}`);
  if (post.has_video) teaser.push("video on Skool");

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={post.title}
        description={gatedDescription(post.summary === post.title ? "" : post.summary, {
          author: post.author_name,
          fallback: `${post.title} — a community write-up published in the AI Automation Base community.`,
        })}
        path={`/community-resources/${post.slug}`}
        type="article"
        publishedTime={post.posted_at}
        modifiedTime={post.last_changed_at ?? post.posted_at}
        author={post.author_name ?? undefined}
        image={post.cover_url ?? undefined}
        jsonLd={jsonLd}
      />
      <main className="container mx-auto px-4 pt-24 pb-20 max-w-5xl">
        <Link to="/community-resources" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Community Resource
        </Link>

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
            <Badge variant="secondary">{monthLabel(post.posted_month)}</Badge>
            {post.pinned ? <Badge variant="outline">Pinned</Badge> : null}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight text-foreground">{post.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span>
              By <span className="font-medium text-foreground">{post.author_name}</span>
            </span>
            <time dateTime={post.posted_at}>{formatPostDate(post.posted_at)}</time>
            <a href={post.skool_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-foreground">
              Open on Skool <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </header>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article>
            {gateLoading ? (
              <p className="text-muted-foreground">Checking access...</p>
            ) : bodyMd !== null ? (
              <>
                {bodyMd.trim() ? (
                  <div className={PROSE}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyMd}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-muted-foreground">This post is just its images and files — see below and the panel.</p>
                )}
                {images.length ? (
                  <div className={`mt-8 grid gap-4 ${images.length > 1 ? "sm:grid-cols-2" : ""}`}>
                    {images.map((img) => (
                      <a
                        key={img.id}
                        href={img.url!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block overflow-hidden rounded-lg border border-border bg-muted"
                      >
                        <img
                          src={img.thumb_url || img.url!}
                          alt={img.file_name || `Image from “${post.title}”`}
                          loading="lazy"
                          width={img.width ?? undefined}
                          height={img.height ?? undefined}
                          className="h-auto w-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="gated-body">
                {post.cover_url ? (
                  <img
                    src={post.cover_url}
                    alt={`Cover image for “${post.title}”`}
                    className="mb-6 w-full max-w-xl rounded-lg border border-border"
                    loading="lazy"
                  />
                ) : null}
                {/* Trimmed to whole sentences — the mirrored teaser ends on an ellipsis
                    mid-clause, and this paragraph is the only text a non-member (or a
                    model) ever sees on this page. */}
                {publicSummary ? (
                  <p className="text-[15px] leading-7 text-foreground/90">{publicSummary}</p>
                ) : null}
                <div className="mt-6 rounded-lg border border-dashed border-border p-5">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                    <Lock className="w-4 h-4 text-primary" /> The full post is for members
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Vault members and members of AI Automations by Hector on Skool can read every community post here,
                    with its images and files.
                  </p>
                </div>
              </div>
            )}
          </article>

          <aside className="lg:sticky lg:top-24 self-start">
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground mb-1">Files &amp; links</h2>
              <p className="text-xs text-muted-foreground mb-4">Mirrored from the Skool post. Members only.</p>

              {gateLoading ? (
                <div className="text-sm text-muted-foreground">Checking access...</div>
              ) : isMember ? (
                <div className="space-y-2">
                  {others.map((a) => (
                    <AttachmentButton key={a.id} a={a} />
                  ))}
                  {others.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {images.length ? "Only images on this post — they are shown with the text." : "Nothing attached to this post."}
                    </p>
                  ) : null}
                </div>
              ) : (
                <MembersOnlyBox user={user} items={teaser} />
              )}
            </div>
          </aside>
        </div>

        {siblings.length ? (
          <section className="mt-16">
            <h2 className="text-xl font-semibold text-foreground mb-4">More from {monthLabel(post.posted_month)}</h2>
            <div className="grid gap-5 sm:grid-cols-3">
              {siblings.map((p) => (
                <Link key={p.slug} to={`/community-resources/${p.slug}`} className="rounded-lg border border-border bg-card p-4 hover:border-primary/60">
                  <div className="font-medium text-foreground leading-snug">{p.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.author_name}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-primary">
                    Open <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
};

export default CommunityResourcePost;
